import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Divider,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import TicketSearch from "../../../../Components/TicketSearch/TicketSearch";

const PatientSearch = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchType, setSearchType] = useState("patient"); // "patient" or "ticket"
  const [selectedTicket, setSelectedTicket] = useState(null);
  const navigate = useNavigate();

  const auth = useAuth();
  const token = auth?.user?.token;

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Use the custom hook to fetch the patient data
  const { data, isLoading, isSuccess, error, isError } = useFetchObjects(
    debouncedSearch && searchType === "patient" ? ["search-patient", debouncedSearch] : null,
    debouncedSearch && searchType === "patient" ? `patients/?search=${debouncedSearch}` : "",
    token,
  );

  // Handle ticket selection
  useEffect(() => {
    if (selectedTicket && selectedTicket.patient) {
      // Navigate to ticket view with ticket and patient data
      navigate(`/patient-profile/ticket/${selectedTicket.id}`, {
        state: {
          ticket: selectedTicket,
          patient: {
            results: [selectedTicket.patient],
          },
        },
      });
    }
  }, [selectedTicket, navigate]);

  // Handle both response structures: array or {results: [...]}
  const patients = React.useMemo(() => {
    if (!data) return [];
    // Check if data is an array
    if (Array.isArray(data)) {
      return data;
    }
    // Check if data has results property
    if (data.results && Array.isArray(data.results)) {
      return data.results;
    }
    return [];
  }, [data]);

  const handlePatientSelect = (patient) => {
    // Wrap patient in the expected structure for Profile component
    const patientData = {
      results: [patient],
    };
    navigate(`/patient-profile/profile`, { state: patientData });
  };

  return (
    <Container>
      <Box
        sx={{
          p: 3,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 3,
          maxWidth: "800px",
          margin: "auto",
          mt: 5,
        }}
      >
        <Typography variant="h6" gutterBottom align="center">
          {t("profile.searchForPatient")}
        </Typography>

        {/* Search Type Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={searchType}
            onChange={(e, newValue) => {
              setSearchType(newValue);
              setSearchQuery("");
              setSelectedTicket(null);
            }}
            centered
          >
            <Tab label={t("profile.searchByTicket") || "Search by Ticket"} value="ticket" />
            <Tab label={t("profile.searchByPatient") || "Search by Patient"} value="patient" />
          </Tabs>
        </Box>

        {/* Loading Spinner */}
        {isLoading && debouncedSearch && searchType === "patient" && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error Handling */}
        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message || t("profile.anError")}
          </Alert>
        )}

        <Grid container spacing={2}>
          {searchType === "ticket" ? (
            <Grid item xs={12}>
              <TicketSearch
                value={selectedTicket}
                onChange={setSelectedTicket}
                placeholder={t("profile.searchByTicketNumber") || "Search by ticket number..."}
                label={t("profile.ticketNumber") || "Ticket Number"}
              />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("profile.enterPatientNameId")}
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                placeholder={t("profile.enterPatientNameId") || "Enter patient name, ID, or phone"}
              />
            </Grid>
          )}
        </Grid>

        {/* Search Results - Patient Search */}
        {searchType === "patient" && debouncedSearch && isSuccess && patients.length > 0 && (
          <Paper
            sx={{
              mt: 2,
              maxHeight: "400px",
              overflow: "auto",
            }}
          >
            <List dense>
              {patients.map((patient, index) => (
                <React.Fragment key={patient.id || index}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handlePatientSelect(patient)}>
                      <ListItemText
                        primary={`${patient.firstname || ""} ${patient.lastname || ""}`.trim() || `Patient #${patient.id}`}
                        secondary={
                          <Box>
                            {patient.phone && (
                              <Typography variant="body2" component="span">
                                {t("profile.phone") || "Phone"}: {patient.phone}
                              </Typography>
                            )}
                            {patient.age && (
                              <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                                {t("profile.age") || "Age"}: {patient.age}
                              </Typography>
                            )}
                            {patient.id && (
                              <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                                ID: {patient.id}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < patients.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {/* Success - No Data - Patient Search */}
        {searchType === "patient" && debouncedSearch && isSuccess && patients.length === 0 && (
          <Typography
            variant="body1"
            color="textSecondary"
            align="center"
            sx={{ mt: 2 }}
          >
            {t("profile.noPatient") || "No patients found"}
          </Typography>
        )}

        {/* Ticket Search Info */}
        {searchType === "ticket" && !selectedTicket && (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mt: 2 }}
          >
            {t("profile.searchTicketInfo") || "Search for a ticket number to view patient information"}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default PatientSearch;
